import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, type FeatureMeta, type User } from './api'

type AuthState = {
  user: User | null
  features: FeatureMeta[]
  loading: boolean
  refresh: () => Promise<void>
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthCtx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [features, setFeatures] = useState<FeatureMeta[]>([])
  const [loading, setLoading] = useState(true)

  async function refresh() {
    try {
      const r = await api.me()
      setUser(r.user)
      setFeatures(r.features)
    } catch {
      setUser(null)
      setFeatures([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  async function login(username: string, password: string) {
    const r = await api.login(username, password)
    setUser(r.user)
    setFeatures(r.features)
  }

  async function logout() {
    await api.logout()
    setUser(null)
    setFeatures([])
  }

  return (
    <AuthCtx.Provider value={{ user, features, loading, refresh, login, logout }}>
      {children}
    </AuthCtx.Provider>
  )
}

export function useAuth(): AuthState {
  const v = useContext(AuthCtx)
  if (!v) throw new Error('useAuth must be used within AuthProvider')
  return v
}
